/**
 * The popovers that change a series (prototype `recurring-lives.html`): move
 * one session, skip it, give it a topic, end the series, and Change the series
 * from its next session on. The webinar's page opens them from its Sessions
 * card and its series card; the board's strip opens Move and Change the series.
 * Each saves through the API and hands back what the server answered.
 */
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { liveLengthChoices } from "../../shared/live-entry";
import { patternProblem, samePattern, type SeriesPattern, type SeriesRule, weekdayOf } from "../../shared/live-series";
import { wallClock } from "../../shared/live-time";
import type { LiveChangeResponse, LiveSessionPatch, LiveSessionView, WebinarLive } from "../../shared/types";
import { useLayout } from "../components/season/layout";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { Sheet } from "../components/ui/Sheet";
import { ApiError, api } from "../lib/api";
import { dayLabel, startPassed, startTimeGroups, timeLabel, todayIn, weekdayShort, weekOf } from "./liveForm";
import { RepeatPicker, repeatSentence } from "./RepeatPicker";
import { patternOfRule } from "./repeats";
import {
  aheadOf,
  changeFrom,
  dayLong,
  dayShort,
  sameEnds,
  seatsHead,
  slotOf,
  timeOf,
  toldLine,
  wallDayShort,
  weekly,
} from "./series";
import "../styles/stage.css";
import "../styles/series.css";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}
const ICON_NOTE = (<Svg><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></Svg>);

/** What a popover hands back once the server has saved: its answer, and what to tell the host. */
export type SeriesSaved = (res: LiveChangeResponse, said: string) => void;

/** Why a save did not go through, in the server's words when it gave any. */
function refusal(e: unknown): string {
  return e instanceof ApiError ? e.message : "That did not save. Try again.";
}

/**
 * The popover's frame: a light card over a scrim, in front of the page. It
 * closes on the scrim or Escape, unless a dropdown or the month is open and
 * Escape is theirs.
 */
interface PopProps {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
}

/** Where a Season sheet's footer is, so the buttons of `Acts` stand in it. Null on the computer. */
const SheetFoot = createContext<{ season: boolean; foot: HTMLElement | null }>({ season: false, foot: null });

/** On a phone and an iPad the same dialog is the kit's sheet: a close X, and the buttons stacked in its footer. */
function SeriesPop(props: PopProps) {
  return useLayout() === "computer" ? <PopCard {...props} /> : <SeasonPop {...props} />;
}

function SeasonPop({ title, sub, onClose, children }: PopProps) {
  const [foot, setFoot] = useState<HTMLElement | null>(null);
  return (
    <Sheet open onClose={onClose} title={title} sub={sub} footer={<span className="srfoot" ref={setFoot} />}>
      {/* Why srin: the popover's rules for the rows inside it (days, chips, notes) apply in the sheet too. */}
      <SheetFoot.Provider value={{ season: true, foot }}>
        <div className="srin">{children}</div>
      </SheetFoot.Provider>
    </Sheet>
  );
}

function PopCard({ title, sub, onClose, children }: PopProps) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".fr-select__list, .rpk-menu, .scrim:not(.srscrim), .ph-sheet-scrim") || box.current?.querySelector(".lcal")) return;
      onClose();
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  return createPortal(
    <div
      className="scrim srscrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="srpop" role="dialog" aria-modal="true" aria-label={title} ref={box}>
        <h3>{title}</h3>
        {sub ? <p className="psub">{sub}</p> : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Who hears about it, when anybody does. */
function SeatNote({ seats, children }: { seats: number; children: ReactNode }) {
  const head = seatsHead(seats);
  if (!head) return null;
  return (
    <div className="pnote">
      {ICON_NOTE}
      <span>
        <b>{head}</b> {children}
      </span>
    </div>
  );
}

function Acts({
  keep,
  go,
  busy,
  ready = true,
  error,
  onKeep,
  onGo,
}: {
  keep: string;
  go: string;
  busy: boolean;
  ready?: boolean;
  error: string;
  onKeep: () => void;
  onGo: () => void;
}) {
  const { season, foot } = useContext(SheetFoot);
  if (season) {
    return (
      <>
        {error ? <p className="pwarn">{error}</p> : null}
        {foot
          ? createPortal(
              <>
                <Button onClick={onKeep}>{keep}</Button>
                <Button variant="p" disabled={!ready || busy} onClick={onGo}>
                  {busy ? "Saving…" : go}
                </Button>
              </>,
              foot,
            )
          : null}
      </>
    );
  }
  return (
    <>
      {error ? <p className="pwarn">{error}</p> : null}
      <div className="pacts">
        <button type="button" className="ghost" onClick={onKeep}>
          {keep}
        </button>
        <button type="button" className="btn" disabled={!ready || busy} onClick={onGo}>
          {busy ? "Saving…" : go}
        </button>
      </div>
    </>
  );
}

/** One save at a time, with the server's reason when it says no. */
function useSave(onClose: () => void): {
  busy: boolean;
  error: string;
  run: (call: () => Promise<LiveChangeResponse>, done: (res: LiveChangeResponse) => void) => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = (call: () => Promise<LiveChangeResponse>, done: (res: LiveChangeResponse) => void) => {
    if (busy) return;
    setBusy(true);
    setError("");
    call()
      .then((res) => {
        done(res);
        onClose();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(refusal(e));
      });
  };
  return { busy, error, run };
}

/** A session as the popovers need it; the board knows no more than this of its current one. */
type SessionAt = Pick<LiveSessionView, "id" | "startsAt" | "lengthMin" | "timezone" | "movedFrom" | "topic">;

/** "that week" for a series with one session a week; "that day" for the rest. */
function unitOf(byWeek: boolean): string {
  return byWeek ? "week" : "day";
}

/** One session: to another day of its week, another start, another length. */
export function MoveSession({
  webinarId,
  session,
  rule,
  seats,
  onSaved,
  onClose,
}: {
  webinarId: string;
  session: SessionAt;
  rule: SeriesRule;
  seats: number;
  onSaved: SeriesSaved;
  onClose: () => void;
}) {
  const tz = session.timezone;
  const slot = slotOf(session);
  const was = wallClock(session.startsAt, tz) ?? { date: slot, time: rule.time };
  const [date, setDate] = useState(was.date);
  const [time, setTime] = useState(was.time);
  const [lengthMin, setLengthMin] = useState(session.lengthMin);
  const { busy, error, run } = useSave(onClose);
  const today = todayIn(tz);
  const passed = startPassed({ date, time, timezone: tz });
  const same = date === was.date && time === was.time && lengthMin === session.lengthMin;
  const day = DAYS[weekdayOf(rule.firstDate)];

  const save = () => {
    const body: LiveSessionPatch = { action: "move", date, time, lengthMin };
    const home = date === slot && time === rule.time;
    const said = `${home ? "Back on" : "Moved to"} ${wallDayShort(date)} at ${timeLabel(time)}.${toldLine(seats)}`;
    run(() => api.board.changeSession(webinarId, session.id, body), (res) => onSaved(res, said));
  };

  return (
    <SeriesPop
      title={`Move only ${wallDayShort(slot)}`}
      sub={
        weekly(rule)
          ? `The other sessions stay on ${day}s at ${timeLabel(rule.time)}.`
          : "The other sessions stay as they are."
      }
      onClose={onClose}
    >
      <div className="pdays" role="group" aria-label="Its day">
        {weekOf(slot).map((d) => (
          <button
            key={d}
            type="button"
            className={[d === date ? "on" : "", d === slot ? "was" : ""].filter(Boolean).join(" ")}
            disabled={d < today}
            aria-label={dayLabel(d)}
            aria-pressed={d === date}
            onClick={() => setDate(d)}
          >
            {weekdayShort(d)}
            <b>{Number(d.slice(8))}</b>
          </button>
        ))}
      </div>
      <div className="lrow">
        <label className="lsel">
          <small>Starts</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="Start time"
            value={time}
            options={startTimeGroups(date, tz)}
            onChange={setTime}
          />
        </label>
        <label className="lsel">
          <small>For</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="How long"
            value={String(lengthMin)}
            options={liveLengthChoices(session.lengthMin).map((l) => ({ value: String(l.min), label: l.label }))}
            onChange={(min) => setLengthMin(Number(min))}
          />
        </label>
      </div>
      {passed ? <p className="pwarn">That time has already passed. Pick a later one.</p> : null}
      <SeatNote seats={seats}>
        {`We'll email them the new time for this ${weekly(rule) ? "week" : "session"} only, with a calendar link for it.`}
      </SeatNote>
      <Acts
        keep="Keep it"
        go="Move this session"
        busy={busy}
        ready={!same && !passed}
        error={error}
        onKeep={onClose}
        onGo={save}
      />
    </SeriesPop>
  );
}

/** One session called off: no room, no recording, and the series carries on. */
export function SkipSession({
  webinarId,
  session,
  next,
  byWeek,
  seats,
  onSaved,
  onClose,
}: {
  webinarId: string;
  session: SessionAt;
  /** The session after it that still happens, for the note. */
  next: SessionAt | null;
  byWeek: boolean;
  seats: number;
  onSaved: SeriesSaved;
  onClose: () => void;
}) {
  const { busy, error, run } = useSave(onClose);
  const unit = unitOf(byWeek);
  const after = next ? `, and that the next one is ${dayLong(next.startsAt, next.timezone)} at ${timeOf(next.startsAt, next.timezone)}` : "";
  return (
    <SeriesPop
      title={`Skip ${dayLong(session.startsAt, session.timezone)}?`}
      sub={`No room, no recording that ${unit}. The series carries on.`}
      onClose={onClose}
    >
      <SeatNote seats={seats}>{`They hear there's no live that ${unit}${after}.`}</SeatNote>
      <Acts
        keep="Keep it"
        go={unit === "week" ? "Skip this week" : "Skip this session"}
        busy={busy}
        error={error}
        onKeep={onClose}
        onGo={() =>
          run(
            () => api.board.changeSession(webinarId, session.id, { action: "skip" }),
            (res) => onSaved(res, `${dayShort(session.startsAt, session.timezone)} skipped.${toldLine(seats)}`),
          )
        }
      />
    </SeriesPop>
  );
}

/** End the series now: the sessions still to come are called off, every replay stays. */
export function EndSeries({
  webinarId,
  toCome,
  seats,
  onSaved,
  onClose,
}: {
  webinarId: string;
  /** Sessions still to come; null for a series with no end, whose planned ones are not the host's count. */
  toCome: number | null;
  seats: number;
  onSaved: SeriesSaved;
  onClose: () => void;
}) {
  const { busy, error, run } = useSave(onClose);
  const lost =
    toCome === 1
      ? "The one session still to come is cancelled."
      : toCome === null
        ? "The sessions still to come are cancelled."
        : `The ${toCome} sessions still to come are cancelled.`;
  return (
    <SeriesPop title="End the series?" sub={`${lost} Past sessions and their replays stay.`} onClose={onClose}>
      <SeatNote seats={seats}>
        They get one email: the series has ended, and every replay stays in their Room. New people can still join to
        watch the replays.
      </SeatNote>
      <Acts
        keep="Keep it going"
        go="End the series"
        busy={busy}
        error={error}
        onKeep={onClose}
        onGo={() => run(() => api.board.endSeries(webinarId), (res) => onSaved(res, `The series has ended.${toldLine(seats)}`))}
      />
    </SeriesPop>
  );
}

/**
 * Change the series from its next session on: the start, the length, how it
 * repeats and when it ends; what happens to the sessions the host
 * moved; and the replay email. Past sessions never change.
 */
export function ChangeSeries({
  webinarId,
  live,
  onSaved,
  onClose,
}: {
  webinarId: string;
  live: WebinarLive;
  onSaved: SeriesSaved;
  onClose: () => void;
}) {
  const series = live.series;
  const [now] = useState(() => Date.now());
  const from = series ? changeFrom(series.sessions, now) : null;
  const rule = series?.rule;
  const base = from ? slotOf(from) : "";
  const [time, setTime] = useState(rule?.time ?? "");
  const [lengthMin, setLengthMin] = useState(rule?.lengthMin ?? 60);
  const [pattern, setPattern] = useState<SeriesPattern | null>(() => (rule ? patternOfRule(rule) : null));
  const [first] = useState(pattern);
  const [keep, setKeep] = useState(true);
  const [replay, setReplay] = useState(series?.replayEmail ?? false);
  const { busy, error, run } = useSave(onClose);
  if (!series || !rule || !from) return null;

  const tz = rule.timezone;
  const when = { date: base, time, timezone: tz, fromSeq: from.seq };
  const problem = pattern ? patternProblem(pattern, base, from.seq) : null;
  const mine = series.sessions.filter((s) => s.seq >= from.seq && aheadOf(s, now));
  const set = mine.filter((s) => s.movedFrom !== null || s.state === "skipped").length;
  const moved = mine.filter((s) => s.movedFrom !== null).length;
  const sameRule =
    !!pattern && !!first && samePattern(pattern, first) && sameEnds(pattern.ends, first.ends) && time === rule.time && lengthMin === rule.lengthMin;
  const ruleChanges = !sameRule || (!keep && moved > 0);
  const replayChanges = replay !== series.replayEmail;
  const said = ruleChanges
    ? `Saved. ${pattern ? repeatSentence(pattern, when) : ""}.${toldLine(series.seats)}`
    : replay
      ? "Saved. People who miss a session get its replay."
      : "Saved. No replay emails.";

  const save = () =>
    pattern &&
    run(
      () =>
        api.board.changeSeries(webinarId, {
          ...pattern,
          date: base,
          time,
          timezone: tz,
          lengthMin,
          keepChanged: keep,
          replayEmail: replay,
        }),
      (res) => onSaved(res, said),
    );

  return (
    <SeriesPop
      title="Change the series"
      sub={`Every session from ${dayShort(from.startsAt, from.timezone)} on. Past sessions never change.`}
      onClose={onClose}
    >
      <div className="lrow">
        <label className="lsel">
          <small>Starts</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="Start time"
            value={time}
            options={startTimeGroups("", tz)}
            onChange={setTime}
          />
        </label>
        <label className="lsel">
          <small>For</small>
          <Select
            className="lselect"
            listClassName="lsel-list"
            ariaLabel="How long"
            value={String(lengthMin)}
            options={liveLengthChoices(rule?.lengthMin ?? lengthMin).map((l) => ({ value: String(l.min), label: l.label }))}
            onChange={(min) => setLengthMin(Number(min))}
          />
        </label>
      </div>
      <RepeatPicker chip noOnce when={when} value={pattern} onChange={setPattern} />
      {problem ? <p className="pwarn">{problem}</p> : null}
      <div className="pwho" role="radiogroup" aria-label="The sessions you moved or skipped">
        <button type="button" role="radio" aria-checked={keep} className={keep ? "on" : ""} onClick={() => setKeep(true)}>
          <span className="rd" aria-hidden="true" />
          <span>
            <b>Keep the ones I moved or skipped</b>
            <small>
              {set === 0
                ? "You haven't moved or skipped any."
                : set === 1
                  ? "1 session keeps what you set for it."
                  : `${set} sessions keep what you set for them.`}
            </small>
          </span>
        </button>
        <button type="button" role="radio" aria-checked={!keep} className={keep ? "" : "on"} onClick={() => setKeep(false)}>
          <span className="rd" aria-hidden="true" />
          <span>
            <b>Put every session on the new time</b>
            <small>Moved sessions come back, skipped ones stay skipped.</small>
          </span>
        </button>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={replay}
        className={replay ? "ptoggle on" : "ptoggle"}
        onClick={() => setReplay((was) => !was)}
      >
        <span className="sw" aria-hidden="true" />
        <span>
          <b>After each session, email the replay to people who missed it</b>
          <small>Only to the people with a seat who didn't come live.</small>
        </span>
      </button>
      {ruleChanges ? <SeatNote seats={series.seats}>We'll email them the new regular time once.</SeatNote> : null}
      <Acts
        keep="Keep it"
        go={ruleChanges || !replayChanges ? "Change every session" : "Save"}
        busy={busy}
        ready={(ruleChanges || replayChanges) && !problem}
        error={error}
        onKeep={onClose}
        onGo={save}
      />
    </SeriesPop>
  );
}
